import React from 'react';
import GiftSelectionModal from '../../gifts/GiftSelectionModal';
import { getProductPrice, formatCurrency } from '../utils/formatting';
import { getImageSrc } from '../utils/imageHelpers';
import { transformPaymentPeriodLabel } from '../../../utils/orderUtils';

export const OrderCartStep = ({
  cart,
  paidItems,
  giftItems,
  selectedCurrency,
  getCartTotal,
  getDiscountedCartTotal,
  discountAmt,
  setDiscountAmt,
  getFormattedCartTotal,
  getFormattedSubtotal,
  updateQuantity,
  removeFromCart,
  removeGift,
  setStep,
  brokenImages,
  setBrokenImages,
  showGiftModal,
  eligibleProducts,
  selectedProductForGifts,
  handleGiftSelectionComplete,
  closeGiftModal,
  openGiftModalForProduct,
  checkoutPaymentPeriod,
  setCheckoutPaymentPeriod,
  installmentFrequency,
  setInstallmentFrequency,
  numberOfInstallments,
  setNumberOfInstallments,
  installmentOverride,
  setInstallmentOverride,
  getSelectedPaymentPeriod,
  PROJECT_ID,
  REGION,
  canApplyDiscount
}) => {
  const FN_BASE = PROJECT_ID ? `https://${REGION}-${PROJECT_ID}.cloudfunctions.net` : '';
  const giftPaymentPeriod = getSelectedPaymentPeriod(giftItems);
  const hasGifts = giftItems.length > 0;
  const currentPeriod = giftPaymentPeriod || checkoutPaymentPeriod;
  const periodDaysMap = { 'day1': 1, 'day30': 30, 'day60': 60, 'day90': 90, 'day180': 180 };
  const currentDays = periodDaysMap[currentPeriod] || 1;
  const maxInstallments = currentPeriod === 'day1' ? 0 : Math.floor(currentDays / 30);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Shopping Cart</h2>

      {cart.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">Your cart is empty</p>
          <button onClick={() => setStep('products')} className="btn btn-primary">
            Continue Shopping
          </button>
        </div>
      ) : (
        <>
          {/* Paid Items */}
          {paidItems.length > 0 && (
            <div className="card mb-6">
              <h3 className="text-lg font-semibold mb-4">Paid Items</h3>
              <div className="space-y-4">
                {paidItems.map((item, index) => {
                  const itemKey = `cart:paid:${item.id}`;
                  const canShowImage = (typeof item.imageUrl === 'string' && item.imageUrl.trim() !== '') && !brokenImages[itemKey];
                  
                  return (
                    <div key={`paid-${index}`} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                      <div className="flex items-center space-x-3 flex-1">
                        {canShowImage ? (
                          <img 
                            src={getImageSrc(item.imageUrl, FN_BASE)}
                            alt={item.name}
                            className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-lg bg-white"
                            onError={() => setBrokenImages(prev => ({ ...prev, [itemKey]: true }))}
                          />
                        ) : (
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-center px-2">
                            <span className="text-xs font-medium text-gray-500">Missing image</span>
                          </div>
                        )}
                        <div>
                          <h4 className="font-medium">{item.name}</h4>
                          <p className="text-sm text-gray-600">{getProductPrice(item, selectedCurrency)} each</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1, false)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1, false)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id, false)}
                          className="ml-4 text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gift Selection Button */}
          <div className="card mb-6">
            <h3 className="text-lg font-semibold mb-4">Gift Options</h3>
            <p className="text-sm text-gray-600 mb-2">
              Eligible products: {eligibleProducts.length} | Cart items: {paidItems.length}
            </p>
            <div className="space-y-2">
              {eligibleProducts.map(product => {
                const supportsExistingPeriod = !giftPaymentPeriod || (product.giftLevel && product.giftLevel[giftPaymentPeriod] > 0);
                
                return (
                <button
                  key={`gift-btn-${product.id}`}
                  disabled={!supportsExistingPeriod}
                  onClick={() => {
                    const existingGifts = giftItems.filter(gift => gift.parentProductId === product.id);
                    existingGifts.forEach(gift => removeGift(gift.id));
                    openGiftModalForProduct(product);
                  }}
                  className={`w-full py-2 rounded-lg transition-colors text-sm ${
                    !supportsExistingPeriod
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                  title={!supportsExistingPeriod ? `Gifts unavailable: Product does not support the selected ${transformPaymentPeriodLabel(giftPaymentPeriod)} period.` : ''}
                >
                  🎁 Select Gifts for {product.name}
                </button>
              )})}
            </div>
          </div>

          {/* Payment Plan Selection */}
          <div className="card mb-6">
            <h3 className="text-lg font-semibold mb-4">Payment Plan</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Period
                    {hasGifts && <span className="text-blue-600 ml-1">(from gifts)</span>}
                  </label>
                  {hasGifts ? (
                    <div className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg bg-blue-50 text-gray-800 font-semibold">
                      {transformPaymentPeriodLabel(currentPeriod)}
                    </div>
                  ) : (
                    <select
                      value={checkoutPaymentPeriod}
                      onChange={(e) => setCheckoutPaymentPeriod(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                    >
                      <option value="day1">Pay full within 2 days</option>
                      <option value="day30">Pay full by end of the month</option>
                      <option value="day60">Pay full within 2 months</option>
                      <option value="day90">Pay full within 3 months</option>
                      <option value="day180">Pay full within 6 months</option>
                    </select>
                  )}
                </div>
                {currentPeriod !== 'day1' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Installment Frequency
                    </label>
                    <select
                      value={installmentFrequency || 'monthly'}
                      onChange={(e) => setInstallmentFrequency(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                )}
              </div>

              {currentPeriod !== 'day1' && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Installments
                      <span className="text-xs text-gray-500 ml-1">(max: {maxInstallments})</span>
                    </label>
                    <input
                      type="number"
                      value={numberOfInstallments}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        const constrainedVal = Math.min(val, maxInstallments);
                        setNumberOfInstallments(String(constrainedVal));
                        setInstallmentOverride('');
                      }}
                      min="1"
                      max={maxInstallments}
                      className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum {maxInstallments} installments (monthly cycle)
                    </p>
                  </div>
                </div>
              )}

              {/* Installment Amount */}
              {currentPeriod !== 'day1' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Installment Amount ({selectedCurrency})
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={installmentOverride || Math.round(getDiscountedCartTotal() / parseInt(numberOfInstallments || '1')).toFixed(0)}
                      onChange={(e) => setInstallmentOverride(e.target.value)}
                      className="flex-1 px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                    />
                    <button
                      onClick={() => setInstallmentOverride('')}
                      className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                      title="Reset to calculated amount"
                    >
                      Reset
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Calculated: {selectedCurrency} {Math.round(getDiscountedCartTotal() / parseInt(numberOfInstallments || '1')).toFixed(0)} (Discounted Total ÷ {numberOfInstallments} installments)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Gift Items */}
          {giftItems.length > 0 && (
            <div className="card mb-6">
              <h3 className="text-lg font-semibold mb-4">Gift Items</h3>
              <div className="space-y-4">
                {giftItems.map((item, index) => {
                  const itemKey = `cart:gift:${item.id}`;
                  const canShowImage = (typeof item.imageUrl === 'string' && item.imageUrl.trim() !== '') && !brokenImages[itemKey];
                  
                  return (
                    <div key={`gift-${index}`} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                      <div className="flex items-center space-x-3 flex-1">
                        {canShowImage ? (
                          <img 
                            src={getImageSrc(item.imageUrl, FN_BASE)}
                            alt={item.name}
                            className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-lg bg-white"
                            onError={() => setBrokenImages(prev => ({ ...prev, [itemKey]: true }))}
                          />
                        ) : (
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-center px-2">
                            <span className="text-xs font-medium text-gray-500">Missing image</span>
                          </div>
                        )}
                        <div>
                          <h4 className="font-medium">{item.name}</h4>
                          <p className="text-sm text-gray-600">Gift Item</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1, true)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1, true)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeGift(item.id)}
                          className="ml-4 text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payment Plan Summary */}
          {currentPeriod !== 'day1' && (
            <div className="card mb-6 bg-blue-50 border-2 border-blue-200">
              <h3 className="text-lg font-semibold mb-3 text-blue-800">
                💳 Payment Plan Summary
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="font-medium text-blue-700">Period (Days):</span>
                  <p className="text-gray-800">{currentDays} Days</p>
                </div>
                <div>
                  <span className="font-medium text-blue-700">Number of Installments:</span>
                  <p className="text-gray-800">{numberOfInstallments}</p>
                </div>
                <div>
                  <span className="font-medium text-blue-700">Installment Amount:</span>
                  <p className="text-gray-800">{selectedCurrency} {installmentOverride || Math.round(getDiscountedCartTotal() / parseInt(numberOfInstallments || '1')).toFixed(0)}</p>
                </div>
                <div>
                  <span className="font-medium text-blue-700">Final Date:</span>
                  <p className="text-gray-800">
                    {(() => {
                      const today = new Date();
                      const futureDate = new Date(today);
                      futureDate.setDate(today.getDate() + currentDays);
                      return futureDate.toISOString().slice(0, 10);
                    })()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Discount Section */}
          {canApplyDiscount && (
            <div className="card mb-6 bg-red-50/50 border border-dashed border-red-200">
              <h3 className="text-lg font-semibold mb-4 text-[#011841] flex items-center">
                🏷️ Discount Amount
              </h3>
              <div className="flex space-x-2">
                <input
                  type="number"
                  value={discountAmt || ''}
                  onChange={(e) => {
                    const value = Math.max(0, parseFloat(e.target.value) || 0);
                    setDiscountAmt(value);
                  }}
                  placeholder="Enter discount amount"
                  className="flex-1 px-4 py-3 border-2 border-red-200 rounded-lg focus:border-red-400 focus:outline-none bg-white text-[#011841]"
                />
                {discountAmt > 0 && (
                  <button
                    onClick={() => setDiscountAmt(0)}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Cart Summary */}
          <div className="card mb-6">
            {discountAmt > 0 ? (
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{getFormattedSubtotal()}</span>
                </div>
                <div className="flex justify-between text-red-600 font-medium">
                  <span>Discount:</span>
                  <span>-{formatCurrency(discountAmt, selectedCurrency)}</span>
                </div>
                <div className="flex justify-between items-center text-lg font-semibold text-[#011841] pt-2 border-t border-gray-100">
                  <span>Total ({paidItems.length} paid items, {giftItems.length} gifts):</span>
                  <span>{getFormattedCartTotal()}</span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>Total ({paidItems.length} paid items, {giftItems.length} gifts):</span>
                <span>{getFormattedCartTotal()}</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex space-x-4">
            <button onClick={() => setStep('products')} className="btn btn-secondary">
              Continue Shopping
            </button>
            <button
              onClick={() => setStep('customer')}
              disabled={paidItems.length === 0}
              className="btn btn-success"
            >
              Customer Details
            </button>
          </div>
        </>
      )}

      {/* Gift Selection Modal */}
      {showGiftModal && (
        <GiftSelectionModal
          products={selectedProductForGifts ? [selectedProductForGifts] : []}
          onComplete={handleGiftSelectionComplete}
          onClose={closeGiftModal}
          existingPeriod={giftPaymentPeriod}
        />
      )}
    </div>
  );
};
